<template>
    <div
        v-if="modelValue"
        class="dsem-backdrop"
        data-testid="dsem-modal"
        @click.self="onCancel"
    >
        <div
            class="dsem-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dsem-title"
        >
            <header class="dsem-header">
                <h3 id="dsem-title" class="dsem-title">
                    Edit "{{ formName }}"
                </h3>
                <button
                    type="button"
                    class="dsem-close"
                    data-testid="dsem-close"
                    aria-label="Close"
                    @click="onCancel"
                >
                    &times;
                </button>
            </header>

            <form class="dsem-body" @submit.prevent="onSave">
                <!-- Metadata section -->
                <section class="dsem-section dsem-section--metadata">
                    <div class="dsem-field">
                        <label class="dsem-label" for="dsem-name">Name</label>
                        <input
                            id="dsem-name"
                            v-model="formName"
                            type="text"
                            class="dsem-input"
                            data-testid="dsem-name"
                            required
                        />
                    </div>
                    <div class="dsem-field">
                        <label
                            class="dsem-label"
                            for="dsem-description"
                        >Description</label>
                        <input
                            id="dsem-description"
                            v-model="formDescription"
                            type="text"
                            class="dsem-input"
                            data-testid="dsem-description"
                        />
                    </div>
                    <div class="dsem-field">
                        <label
                            class="dsem-label"
                            for="dsem-client-tag"
                        >Client tag (optional)</label>
                        <input
                            id="dsem-client-tag"
                            v-model="formClientTag"
                            type="text"
                            class="dsem-input"
                            placeholder="e.g. acme"
                            data-testid="dsem-client-tag"
                        />
                    </div>
                </section>

                <!-- Placements section -->
                <section class="dsem-section dsem-section--placements">
                    <header class="dsem-section__header">
                        <h4 class="dsem-section__title">
                            Placements ({{ formPlacements.length }})
                        </h4>
                        <button
                            type="button"
                            class="dsem-btn dsem-btn--secondary"
                            data-testid="dsem-add-placement"
                            @click="onAddPlacement"
                        >
                            + Add placement
                        </button>
                    </header>

                    <div
                        v-if="!formPlacements.length"
                        class="dsem-empty"
                        data-testid="dsem-placements-empty"
                    >
                        No placements yet. Add one to start building the
                        set.
                    </div>

                    <ol v-else class="dsem-placement-list">
                        <li
                            v-for="(p, idx) in formPlacements"
                            :key="p._localId"
                            class="dsem-placement-card"
                            :data-testid="`dsem-placement-${idx}`"
                        >
                            <div class="dsem-placement-card__head">
                                <span class="dsem-placement-card__num">
                                    Placement {{ idx + 1 }}
                                </span>
                                <div class="dsem-placement-card__actions">
                                    <button
                                        type="button"
                                        class="dsem-icon-btn"
                                        :disabled="idx === 0"
                                        :data-testid="`dsem-move-up-${idx}`"
                                        aria-label="Move placement up"
                                        @click="onMoveUp(idx)"
                                    >
                                        &uarr;
                                    </button>
                                    <button
                                        type="button"
                                        class="dsem-icon-btn"
                                        :disabled="
                                            idx === formPlacements.length - 1
                                        "
                                        :data-testid="`dsem-move-down-${idx}`"
                                        aria-label="Move placement down"
                                        @click="onMoveDown(idx)"
                                    >
                                        &darr;
                                    </button>
                                    <button
                                        type="button"
                                        class="dsem-icon-btn dsem-icon-btn--danger"
                                        :data-testid="`dsem-remove-${idx}`"
                                        aria-label="Remove placement"
                                        @click="onRemovePlacement(idx)"
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>

                            <div class="dsem-placement-card__row">
                                <label class="dsem-label">Zone</label>
                                <select
                                    v-model.number="p.placement_zone_id"
                                    class="dsem-input"
                                    :data-testid="`dsem-zone-${idx}`"
                                >
                                    <option :value="null">Choose zone...</option>
                                    <option
                                        v-for="z in zones"
                                        :key="z.id"
                                        :value="z.id"
                                    >
                                        {{ z.display_name }}
                                    </option>
                                </select>
                            </div>

                            <div class="dsem-placement-card__row">
                                <label class="dsem-label">Artwork</label>
                                <select
                                    v-model.number="p.artwork_id"
                                    class="dsem-input"
                                    :data-testid="`dsem-artwork-${idx}`"
                                >
                                    <option :value="null">Choose artwork...</option>
                                    <option
                                        v-for="a in artworks"
                                        :key="a.id"
                                        :value="a.id"
                                    >
                                        {{ artworkLabel(a) }}
                                    </option>
                                </select>
                            </div>

                            <div class="dsem-placement-card__row dsem-placement-card__row--inline">
                                <div class="dsem-field dsem-field--inline">
                                    <label class="dsem-label">Width (in)</label>
                                    <input
                                        v-model.number="p.imprint_width_in"
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        class="dsem-input dsem-input--narrow"
                                        :data-testid="`dsem-width-${idx}`"
                                    />
                                </div>
                                <div class="dsem-field dsem-field--inline">
                                    <label class="dsem-label">Height (in)</label>
                                    <input
                                        v-model.number="p.imprint_height_in"
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        class="dsem-input dsem-input--narrow"
                                        :data-testid="`dsem-height-${idx}`"
                                    />
                                </div>
                            </div>

                            <div class="dsem-placement-card__row">
                                <label class="dsem-label">
                                    Personalization keys (comma-separated)
                                </label>
                                <input
                                    v-model="p._persoText"
                                    type="text"
                                    class="dsem-input"
                                    placeholder="e.g. name, monogram"
                                    :data-testid="`dsem-perso-${idx}`"
                                />
                            </div>
                        </li>
                    </ol>
                </section>

                <p
                    v-if="saveError"
                    class="dsem-error"
                    data-testid="dsem-error"
                >
                    {{ saveError }}
                </p>

                <footer class="dsem-footer">
                    <button
                        type="button"
                        class="dsem-btn dsem-btn--ghost"
                        data-testid="dsem-cancel"
                        :disabled="saving"
                        @click="onCancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="dsem-btn dsem-btn--primary"
                        data-testid="dsem-save"
                        :disabled="saving"
                    >
                        {{ saving ? 'Saving...' : 'Save changes' }}
                    </button>
                </footer>
            </form>
        </div>
    </div>
</template>

<script>
/**
 * DecorationSetEditModal.vue -- Phase 4.10 placements editor.
 *
 * Card: 2026-06-13 design bundle implementation --
 * https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.10 -- DecorationSetEditModal (placements editor).
 *
 * Opened from DecorationSetDetailPage.vue's Edit button. Loads the set's
 * current state into a local form so the operator can edit name +
 * description + client_tag and the full placement list (add / remove /
 * reorder / edit zone, artwork, sizing, perso fields) without round-
 * tripping per keystroke. The Save action diffs the local form against
 * the initial server state and issues PATCH / POST / DELETE calls in
 * the order that minimises wire traffic.
 *
 * Wire contract
 * -------------
 * The store actions wrap these backend endpoints (decoration_sets.py):
 *   - PATCH  /decoration_sets/<id>/                  (set metadata)
 *   - POST   /decoration_sets/<id>/placements/       (new row)
 *   - PATCH  /decoration_sets/<id>/placements/<pid>/ (edit row + reorder)
 *   - DELETE /decoration_sets/<id>/placements/<pid>/ (remove row)
 *
 * Why no atomic "save everything" endpoint? The backend's per-row write
 * surface lands in PR #1561; the editor operates against that surface
 * directly rather than reshaping to a bespoke bulk endpoint. The save
 * sequence is:
 *   1. Metadata PATCH (only if changed).
 *   2. DELETE removed placements.
 *   3. PATCH edited placements.
 *   4. POST new placements.
 *   5. PATCH each placement with its final ``display_order`` (covers
 *      reorder + newly added).
 *
 * Inputs
 * ------
 * - ``modelValue`` (Boolean) -- visibility, two-way via ``update:modelValue``.
 * - ``setId`` (Number/String) -- the set being edited. The modal reads
 *   ``store.detail`` directly (assumes the parent has already hydrated
 *   it via fetchDetail).
 *
 * Events
 * ------
 * - ``update:modelValue`` -- visibility toggle.
 * - ``saved`` -- emitted after a successful save; the parent uses it to
 *   refetch the detail page so the rendered state reflects the new
 *   server-authoritative ordering.
 *
 * Capability
 * ----------
 * No client-side cap check -- the router gates the parent page on
 * ``distributor.decoration_library.write`` and the backend re-enforces
 * the cap on every mutation. The modal does not surface a "view-only"
 * mode.
 */
import { formatArtworkName } from '@/utils/formatArtworkName'
import { useArtworksStore } from '@/stores/artworks'
import { useDecorationSetsStore } from '@/stores/decorationSets'

// Local id sequence used to key new placement rows in the template
// before they get a server-assigned ``id``. Reset per modal open.
let LOCAL_SEQ = 0
function nextLocalId() {
    LOCAL_SEQ += 1
    return `new-${LOCAL_SEQ}`
}

function persoKeysToText(keys) {
    return Array.isArray(keys) ? keys.join(', ') : ''
}

function persoTextToKeys(text) {
    if (typeof text !== 'string') return []
    return text
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
}

export default {
    name: 'DecorationSetEditModal',

    props: {
        modelValue: { type: Boolean, default: false },
        setId: { type: [Number, String], required: true },
    },

    emits: ['update:modelValue', 'saved'],

    data() {
        return {
            store: useDecorationSetsStore(),
            artworksStore: useArtworksStore(),
            formName: '',
            formDescription: '',
            formClientTag: '',
            // Each entry: { _localId, id (or null for new), placement_zone_id,
            //   artwork_id, artwork_version_id, imprint_width_in,
            //   imprint_height_in, perso_field_keys, sizing_metadata,
            //   _persoText, _origin }
            formPlacements: [],
            initialSnapshot: null,
            saving: false,
            saveError: null,
        }
    },

    computed: {
        zones() {
            return this.store.zones || []
        },
        artworks() {
            return this.artworksStore.artworks || []
        },
        isDirty() {
            return JSON.stringify(this._snapshotForDiff())
                !== JSON.stringify(this.initialSnapshot)
        },
    },

    watch: {
        modelValue(now) {
            if (now) {
                this.hydrateForm()
                this.store.fetchZones()
                // Artworks store may already be hydrated by the editor;
                // fetch defensively (the store is idempotent — it
                // re-fetches on each call, but the modal cost is one
                // extra GET on open).
                if (
                    !this.artworksStore.artworks
                    || this.artworksStore.artworks.length === 0
                ) {
                    if (typeof this.artworksStore.fetch === 'function') {
                        this.artworksStore.fetch()
                    }
                }
            }
        },
    },

    mounted() {
        if (this.modelValue) {
            this.hydrateForm()
            this.store.fetchZones()
        }
    },

    methods: {
        artworkLabel(a) {
            return formatArtworkName(a.filename) || `Artwork #${a.id}`
        },

        hydrateForm() {
            const set = this.store.detail
            if (!set) {
                this.formName = ''
                this.formDescription = ''
                this.formClientTag = ''
                this.formPlacements = []
                this.initialSnapshot = this._snapshotForDiff()
                return
            }
            this.formName = set.name || ''
            this.formDescription = set.description || ''
            this.formClientTag = set.client_tag || ''
            const sourcePlacements = Array.isArray(set.placements)
                ? [...set.placements]
                : []
            sourcePlacements.sort(
                (a, b) =>
                    (a.display_order ?? 0) - (b.display_order ?? 0),
            )
            this.formPlacements = sourcePlacements.map((p) => ({
                _localId: `existing-${p.id}`,
                id: p.id,
                placement_zone_id: p.placement_zone_id ?? null,
                artwork_id: p.artwork_id ?? null,
                artwork_version_id: p.artwork_version_id ?? null,
                imprint_width_in: p.imprint_width_in ?? null,
                imprint_height_in: p.imprint_height_in ?? null,
                perso_field_keys: Array.isArray(p.perso_field_keys)
                    ? [...p.perso_field_keys]
                    : [],
                sizing_metadata: p.sizing_metadata || null,
                _persoText: persoKeysToText(p.perso_field_keys),
                _origin: 'server',
            }))
            this.initialSnapshot = this._snapshotForDiff()
            this.saveError = null
        },

        _snapshotForDiff() {
            // Deterministic, comparable shape for the dirty-check. Drop
            // _persoText (derived) but keep the parsed keys.
            return {
                name: this.formName.trim(),
                description: this.formDescription || '',
                client_tag: this.formClientTag || '',
                placements: this.formPlacements.map((p) => ({
                    id: p.id ?? null,
                    placement_zone_id: p.placement_zone_id ?? null,
                    artwork_id: p.artwork_id ?? null,
                    artwork_version_id: p.artwork_version_id ?? null,
                    imprint_width_in:
                        p.imprint_width_in === '' || p.imprint_width_in == null
                            ? null
                            : Number(p.imprint_width_in),
                    imprint_height_in:
                        p.imprint_height_in === ''
                        || p.imprint_height_in == null
                            ? null
                            : Number(p.imprint_height_in),
                    perso_field_keys: persoTextToKeys(p._persoText),
                })),
            }
        },

        onAddPlacement() {
            this.formPlacements.push({
                _localId: nextLocalId(),
                id: null,
                placement_zone_id: null,
                artwork_id: null,
                artwork_version_id: null,
                imprint_width_in: null,
                imprint_height_in: null,
                perso_field_keys: [],
                sizing_metadata: null,
                _persoText: '',
                _origin: 'new',
            })
        },

        onRemovePlacement(idx) {
            if (idx < 0 || idx >= this.formPlacements.length) return
            this.formPlacements.splice(idx, 1)
        },

        onMoveUp(idx) {
            if (idx <= 0) return
            const arr = this.formPlacements
            const tmp = arr[idx - 1]
            arr.splice(idx - 1, 1, arr[idx])
            arr.splice(idx, 1, tmp)
        },

        onMoveDown(idx) {
            if (idx >= this.formPlacements.length - 1) return
            const arr = this.formPlacements
            const tmp = arr[idx + 1]
            arr.splice(idx + 1, 1, arr[idx])
            arr.splice(idx, 1, tmp)
        },

        async onSave() {
            if (this.saving) return
            this.saving = true
            this.saveError = null
            try {
                await this._performSave()
                this.$emit('saved')
                this.$emit('update:modelValue', false)
            } catch (e) {
                this.saveError =
                    e?.response?.data?.error
                    || e?.message
                    || 'Save failed.'
            } finally {
                this.saving = false
            }
        },

        async _performSave() {
            const set = this.store.detail
            if (!set) {
                throw new Error('No set loaded.')
            }
            const setId = this.setId

            // 1. Metadata patch (only if any field changed).
            const metaPatch = {}
            const trimmedName = this.formName.trim()
            if (trimmedName !== (set.name || '')) metaPatch.name = trimmedName
            if ((this.formDescription || '') !== (set.description || '')) {
                metaPatch.description = this.formDescription || null
            }
            if ((this.formClientTag || '') !== (set.client_tag || '')) {
                metaPatch.client_tag = this.formClientTag || null
            }
            if (Object.keys(metaPatch).length > 0) {
                await this.store.updateSet(setId, metaPatch)
            }

            // 2. DELETE removed placements (those in the server state
            //    that no longer appear in the form).
            const formIds = new Set(
                this.formPlacements
                    .map((p) => (p.id == null ? null : Number(p.id)))
                    .filter((x) => x != null),
            )
            const removed = (set.placements || []).filter(
                (p) => !formIds.has(Number(p.id)),
            )
            for (const r of removed) {
                await this.store.removePlacement(setId, r.id)
            }

            // 3. PATCH edited placements (existing rows with changes).
            const serverIndex = new Map(
                (set.placements || []).map((p) => [Number(p.id), p]),
            )
            for (const p of this.formPlacements) {
                if (p.id == null) continue
                const original = serverIndex.get(Number(p.id))
                if (!original) continue
                const patch = this._diffPlacement(original, p)
                if (Object.keys(patch).length > 0) {
                    await this.store.updatePlacement(setId, p.id, patch)
                }
            }

            // 4. POST new placements.
            for (let idx = 0; idx < this.formPlacements.length; idx += 1) {
                const p = this.formPlacements[idx]
                if (p.id != null) continue
                const created = await this.store.addPlacement(
                    setId,
                    this._placementPayload(p),
                )
                if (created && created.id != null) {
                    p.id = created.id
                }
            }

            // 5. Reorder pass: rewrite display_order for the final
            //    sequence. We always run this when the form had any
            //    structural change (add / remove / reorder).
            const finalIds = this.formPlacements
                .map((p) => p.id)
                .filter((x) => x != null)
            if (finalIds.length > 0) {
                await this.store.reorderPlacements(setId, finalIds)
            }
        },

        _diffPlacement(original, form) {
            const patch = {}
            const fields = [
                'placement_zone_id',
                'artwork_id',
                'artwork_version_id',
                'imprint_width_in',
                'imprint_height_in',
            ]
            for (const k of fields) {
                const a = original[k] ?? null
                const b = form[k] === '' || form[k] == null ? null : form[k]
                if (a !== b) patch[k] = b
            }
            const formKeys = persoTextToKeys(form._persoText)
            const origKeys = Array.isArray(original.perso_field_keys)
                ? original.perso_field_keys
                : []
            if (formKeys.join(',') !== origKeys.join(',')) {
                patch.perso_field_keys = formKeys
            }
            return patch
        },

        _placementPayload(form) {
            return {
                placement_zone_id: form.placement_zone_id ?? null,
                artwork_id: form.artwork_id ?? null,
                artwork_version_id: form.artwork_version_id ?? null,
                imprint_width_in:
                    form.imprint_width_in === ''
                    || form.imprint_width_in == null
                        ? null
                        : Number(form.imprint_width_in),
                imprint_height_in:
                    form.imprint_height_in === ''
                    || form.imprint_height_in == null
                        ? null
                        : Number(form.imprint_height_in),
                perso_field_keys: persoTextToKeys(form._persoText),
            }
        },

        onCancel() {
            if (this.saving) return
            if (this.isDirty) {
                const ok =
                    typeof window !== 'undefined'
                    && typeof window.confirm === 'function'
                        ? window.confirm(
                              'Discard unsaved changes?',
                          )
                        : true
                if (!ok) return
            }
            this.$emit('update:modelValue', false)
        },
    },
}
</script>

<style scoped>
.dsem-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.42);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 16px;
    z-index: 1100;
    overflow-y: auto;
}

.dsem-modal {
    background: #fff;
    border-radius: 8px;
    width: 100%;
    max-width: 760px;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
}

.dsem-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dsem-title {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
}

.dsem-close {
    background: none;
    border: 0;
    cursor: pointer;
    font-size: 22px;
    color: rgba(0, 0, 0, 0.55);
}

.dsem-body {
    padding: 16px 20px 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.dsem-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dsem-section--placements {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    padding-top: 14px;
}

.dsem-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dsem-section__title {
    margin: 0;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
}

.dsem-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.dsem-field--inline {
    flex: 1;
}

.dsem-label {
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
}

.dsem-input {
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    padding: 7px 10px;
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
}

.dsem-input--narrow {
    max-width: 160px;
}

.dsem-empty {
    padding: 18px;
    text-align: center;
    color: rgba(0, 0, 0, 0.55);
    background: rgba(0, 0, 0, 0.02);
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    font-size: 13px;
}

.dsem-placement-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dsem-placement-card {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: #fafbfc;
}

.dsem-placement-card__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.dsem-placement-card__num {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.7);
}

.dsem-placement-card__actions {
    display: flex;
    gap: 6px;
}

.dsem-placement-card__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.dsem-placement-card__row--inline {
    flex-direction: row;
    gap: 14px;
}

.dsem-icon-btn {
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    border-radius: 4px;
    width: 26px;
    height: 26px;
    cursor: pointer;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
}

.dsem-icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.dsem-icon-btn--danger {
    color: #c62828;
    border-color: rgba(198, 40, 40, 0.4);
}

.dsem-error {
    margin: 0;
    color: #d32f2f;
    font-size: 13px;
}

.dsem-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.dsem-btn {
    border-radius: 4px;
    border: 1px solid rgba(25, 118, 210, 0.4);
    background: #fff;
    color: #1976d2;
    padding: 7px 14px;
    font-size: 13px;
    cursor: pointer;
}

.dsem-btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}

.dsem-btn--ghost {
    background: #fff;
    color: rgba(0, 0, 0, 0.7);
    border-color: rgba(0, 0, 0, 0.2);
}

.dsem-btn--secondary {
    background: #fff;
    color: #1976d2;
    border-color: rgba(25, 118, 210, 0.4);
    font-size: 12px;
    padding: 5px 10px;
}

.dsem-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
